import { ProductColor } from '@components/Product/Details/_ProductSelection';
import { LinkProps } from '@repo/ui/src/types/link';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MediaType } from '@repo/ui/src/types/image';

export const COLOR_STYLES = {
  bronze: 'bg-bronzecircle',
  blue: 'bg-[#1A2252]',
  red: 'bg-red-400',
  black: 'bg-neutral-500',
  '': '',
};

interface ColorItemProps {
  color: keyof typeof COLOR_STYLES;
  link?: LinkProps;
  image?: MediaType;
}

interface ItemProps {
  color: keyof typeof COLOR_STYLES;
  selected?: boolean;
  image?: MediaType;
}
const Item = ({ color, selected = false, image }: ItemProps) => (
  <p className="flex pr-6 items-center cursor-pointer hover:text-brand-300" aria-current={selected}>
    <span
      className={classNames(
        'flex justify-center items-center mr-2 w-7 h-7 rounded-full -rotate-90 bg-[#f6f4f3]',
        selected && 'border-2 border-brand-300',
      )}
    >
      {image ? (
        <img alt="" className="w-5 h-5 rounded-full object-cover object-fit" src={image?.url} />
      ) : (
        <span
          className={classNames(
            'w-5 h-5 rounded-full',
            color && COLOR_STYLES[color?.toLowerCase() as keyof typeof COLOR_STYLES],
          )}
        />
      )}
    </span>
    <label className="cursor-pointer hover:text-brand-300">{color}</label>
  </p>
);

const ColorItem = ({ color, link, image }: ColorItemProps) => {
  const linkObj = link && buildLinkObj(link);

  if (linkObj) {
    return (
      <a href={linkObj.href}>
        <Item color={color} />
      </a>
    );
  }

  return <Item selected color={color} image={image} />;
};

export interface AlternateColorsProps {
  color: keyof typeof COLOR_STYLES;
  colors: Array<ProductColor>;
  dark?: boolean;
  step?: number | `${number}`;
}

const AlternateColors = ({ color, colors, step, dark = false }: AlternateColorsProps) => {
  const { t } = useTranslation();
  const altColors = [{ color }, ...colors];
  return (
    <div className={classNames(dark && 'text-white')}>
      <p className={classNames('tra-text-web-utl-link', dark && 'text-white')}>
        0{step} -{' '}
        {t('productDetailPageContent.selectColorText', undefined, { default: 'Select A Color' })}:{' '}
        {color}
      </p>
      <p className="sr-only">
        {altColors?.length}{' '}
        {t('productDetailPageContent.colorsAvailableText', undefined, {
          default: 'colors available',
        })}
        ,{' '}
        {t('productDetailPageContent.selectedOptionText', undefined, {
          default: 'Selected option is',
        })}{' '}
        {color}
      </p>
      <div className="pt-2 pb-6 flex align-start">
        {altColors?.map((item) => (
          <ColorItem {...item} key={`grill_color_${item.color}`} />
        ))}
      </div>
    </div>
  );
};

export default AlternateColors;
